/** Optional public event content. Stored as one replaceable group so its
 * bounded size is checked before it reaches the registry's single KV row. */
import { isLocalDateTime } from "./tournament";
import { wallClock } from "./wall-clock";

export const INFORMATION_TEXT_MAX = 10_000;
export const INFORMATION_BYTES_MAX = 48_000;
export const MEDIA_PEOPLE_MAX = 12;
export const MEDIA_PROFILES_MAX = 5;
export const MEDIA_ALBUMS_MAX = 20;
export const MEDIA_NAME_MAX = 120;
export const MEDIA_NOTES_MAX = 1_000;
export const MEDIA_URL_MAX = 2_048;
export const MEDIA_ROLES = ["photographer", "videographer", "both"] as const;
export type MediaRole = (typeof MEDIA_ROLES)[number];

export interface MediaLink {
  id: string;
  url: string;
  title?: string;
  /** Public download instructions, PIN, credit/reuse terms, etc. */
  notes?: string;
}
export interface MediaContributor {
  id: string;
  role: MediaRole;
  name?: string;
  profiles: MediaLink[];
  /** Independent links: different days, albums or edits need no new person. */
  albums: MediaLink[];
}
export interface TournamentInformation {
  text: string;
  media: MediaContributor[];
  details?: TournamentDetails;
  announcements?: TournamentAnnouncement[];
}

export function hasTournamentInformation(info: TournamentInformation | undefined): boolean {
  return Boolean(info && (info.text.trim() || info.media.length || info.announcements?.length ||
    Object.values(info.details ?? {}).some((value) => value.trim())));
}

/** One bounded field catalog drives the optional editor and public sections.
 * Amenity descriptions distinguish unavailable facilities from unknown ones. */
export const TOURNAMENT_DETAIL_GROUPS = [
  { key: "organiser", fields: [
    { key: "organiserName", kind: "short", max: 120 },
    { key: "organiserPhone", kind: "phone", max: 40 },
    { key: "organiserEmail", kind: "email", max: 254 },
    { key: "organiserLink", kind: "url", max: MEDIA_URL_MAX },
  ] },
  { key: "firstAid", fields: [
    { key: "firstAidLocation", kind: "text", max: 1000 },
    { key: "firstAidName", kind: "short", max: 120 },
    { key: "firstAidPhone", kind: "phone", max: 40 },
    { key: "firstAidEmail", kind: "email", max: 254 },
    { key: "firstAidLink", kind: "url", max: MEDIA_URL_MAX },
  ] },
  { key: "lostFound", fields: [
    { key: "lostFoundInstructions", kind: "text", max: 2000 },
    { key: "lostFoundName", kind: "short", max: 120 },
    { key: "lostFoundPhone", kind: "phone", max: 40 },
    { key: "lostFoundEmail", kind: "email", max: 254 },
    { key: "lostFoundLink", kind: "url", max: MEDIA_URL_MAX },
  ] },
  { key: "keyTimes", fields: [
    { key: "checkIn", kind: "datetime", max: 16 },
    { key: "captainsMeeting", kind: "datetime", max: 16 },
    { key: "awardsCeremony", kind: "datetime", max: 16 },
  ] },
  { key: "amenities", fields: [
    { key: "showers", kind: "text", max: 1000 },
    { key: "changingRooms", kind: "text", max: 1000 },
    { key: "food", kind: "text", max: 1000 },
    { key: "accessibility", kind: "text", max: 1000 },
  ] },
  { key: "fees", fields: [
    { key: "feeAmount", kind: "short", max: 240 },
    { key: "feeIncludes", kind: "text", max: 2000 },
    { key: "paymentInstructions", kind: "text", max: 2000 },
    { key: "paymentDeadline", kind: "datetime", max: 16 },
  ] },
] as const;
export type TournamentDetailField = (typeof TOURNAMENT_DETAIL_GROUPS)[number]["fields"][number];
export type TournamentDetails = Partial<Record<TournamentDetailField["key"], string>>;

/** Contact URIs cannot carry query parameters, headers or arbitrary schemes. */
export function informationContactHref(kind: "phone" | "email", value: string): string | null {
  const text = value.trim();
  if (kind === "email") {
    return text.length <= 254 && /^[^\s@<>?&#]+@[^\s@<>?&#]+\.[^\s@<>?&#]+$/.test(text)
      ? `mailto:${encodeURIComponent(text).replace(/%40/g, "@")}` : null;
  }
  const digits = text.replace(/[\s().-]/g, "");
  return text.length <= 40 && /^\+?\d{3,20}$/.test(digits) ? `tel:${digits}` : null;
}

/** Empty fields are omitted. A malformed known field refuses the whole update;
 * unknown keys are discarded so clients cannot add arbitrary public sections. */
export function parseTournamentDetails(value: unknown): TournamentDetails | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const input = value as Record<string, unknown>;
  const result: TournamentDetails = {};
  for (const group of TOURNAMENT_DETAIL_GROUPS) {
    for (const field of group.fields) {
      const raw = input[field.key];
      if (raw === undefined || raw === null) continue;
      if (typeof raw !== "string" || raw.length > field.max) return null;
      const text = raw.trim();
      if (!text) continue;
      if (field.kind === "datetime" && !isLocalDateTime(text)) return null;
      if ((field.kind === "phone" || field.kind === "email") && !informationContactHref(field.kind, text)) return null;
      if (field.kind === "url") {
        const url = informationUrl(text);
        if (!url) return null;
        result[field.key] = url;
      } else result[field.key] = text;
    }
  }
  return result;
}

/** Accept pasted domains too. Never turn arbitrary schemes, credentials or
 * control characters into links; no remote previews or embeds are fetched. */
export function informationUrl(value: string): string | null {
  const raw = value.trim();
  if (!raw || raw.length > MEDIA_URL_MAX || /[\s\\\u0000-\u001f\u007f]/.test(raw)) return null;
  const candidate = /^https?:\/\//i.test(raw) ? raw : /^[\p{L}\d][\p{L}\d.-]*\.[\p{L}]{2,}(?::\d+)?(?:[/?#]|$)/u.test(raw) ? `https://${raw}` : null;
  if (!candidate) return null;
  try {
    const url = new URL(candidate);
    return (url.protocol === "https:" || url.protocol === "http:") && url.hostname &&
      !url.username && !url.password && url.href.length <= MEDIA_URL_MAX ? url.href : null;
  } catch { return null; }
}

/** Host matching is exact or a genuine subdomain, never instagram.com.evil. */
export function informationLinkSite(href: string): string {
  const url = informationUrl(href);
  if (!url) return href;
  const host = new URL(url).hostname.replace(/^www\./, "");
  const sites: [string, string][] = [
    ["instagram.com", "Instagram"], ["youtube.com", "YouTube"], ["youtu.be", "YouTube"],
    ["facebook.com", "Facebook"], ["fb.com", "Facebook"], ["tiktok.com", "TikTok"],
    ["t.me", "Telegram"], ["telegram.me", "Telegram"], ["vimeo.com", "Vimeo"],
    ["flickr.com", "Flickr"], ["drive.google.com", "Google Drive"],
    ["photos.google.com", "Google Photos"], ["photos.app.goo.gl", "Google Photos"],
    ["dropbox.com", "Dropbox"], ["pixieset.com", "Pixieset"], ["x.com", "X"],
  ];
  return sites.find(([domain]) => host === domain || host.endsWith(`.${domain}`))?.[1] ?? host;
}

export interface InformationTextPart { text: string; href?: string }
/** Preserve the organiser's prose and punctuation. React renders every text
 * node escaped; links are the only inline formatting this parser creates. */
export function informationTextParts(text: string): InformationTextPart[] {
  const parts: InformationTextPart[] = [];
  const regex = /(?:https?:\/\/|www\.)[^\s<>"“”]+/gi;
  let start = 0;
  for (const match of text.matchAll(regex)) {
    const at = match.index;
    let link = match[0].replace(/[.,!?;:]+$/, "");
    // Leave balanced parentheses in e.g. Wikipedia paths; strip prose closers.
    for (const [open, close] of [["(", ")"], ["[", "]"], ["{", "}"]] as const) {
      while (link.endsWith(close) && link.split(close).length > link.split(open).length) link = link.slice(0, -1);
    }
    const href = informationUrl(link);
    if (!href) continue;
    if (at > start) parts.push({ text: text.slice(start, at) });
    parts.push({ text: link, href });
    start = at + link.length;
  }
  if (start < text.length) parts.push({ text: text.slice(start) });
  return parts;
}

export interface InformationBlock { kind: "paragraph" | "list"; lines: string[] }
/** Plain paragraphs and pasted bullet points, without a Markdown dependency. */
export function informationBlocks(text: string): InformationBlock[] {
  const blocks: InformationBlock[] = [];
  let current: InformationBlock | undefined;
  for (const line of text.replace(/\r\n?/g, "\n").split("\n")) {
    if (!line.trim()) { current = undefined; continue; }
    const bullet = /^\s*[-*•]\s+(.+)$/.exec(line);
    const kind = bullet ? "list" : "paragraph";
    if (!current || current.kind !== kind) {
      current = { kind, lines: [] };
      blocks.push(current);
    }
    current.lines.push(bullet?.[1] ?? line);
  }
  return blocks;
}

export const ANNOUNCEMENTS_MAX = 8;
export const ANNOUNCEMENT_TITLE_MAX = 120;
export const ANNOUNCEMENT_TEXT_MAX = 2000;
export interface TournamentAnnouncement {
  id: string;
  title: string;
  text: string;
  pinned: boolean;
  /** Wall-clock minute in the tournament timezone, never the viewer's. */
  expiresAt?: string;
  /** Server-owned instant, refreshed only when this notice changes. */
  updatedAt?: number;
}

export function parseAnnouncements(value: unknown): TournamentAnnouncement[] | null {
  if (!Array.isArray(value) || value.length > ANNOUNCEMENTS_MAX) return null;
  const result: TournamentAnnouncement[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    const item = raw as Record<string, unknown>;
    const { id, title, text, pinned, expiresAt, updatedAt } = item;
    if (typeof id !== "string" || !id.trim() || id.length > 64 ||
      typeof title !== "string" || !title.trim() || title.length > ANNOUNCEMENT_TITLE_MAX ||
      typeof text !== "string" || !text.trim() || text.length > ANNOUNCEMENT_TEXT_MAX ||
      typeof pinned !== "boolean" || result.some((notice) => notice.id === id) ||
      (expiresAt !== undefined && (typeof expiresAt !== "string" || (expiresAt !== "" && !isLocalDateTime(expiresAt)))) ||
      (updatedAt !== undefined && (typeof updatedAt !== "number" || !Number.isSafeInteger(updatedAt) || updatedAt < 0 || updatedAt > 8_640_000_000_000_000))) return null;
    result.push({ id, title: title.trim(), text: text.trim(), pinned,
      ...(expiresAt ? { expiresAt } : {}), ...(updatedAt !== undefined ? { updatedAt } : {}) });
  }
  return result;
}

/** Called inside the registry write. Client timestamps never set publication
 * history; editing an unrelated field does not make an old notice look new. */
export function stampAnnouncements(next: TournamentInformation, previous: TournamentInformation | undefined, now: number): TournamentInformation {
  if (!next.announcements) return next;
  return { ...next, announcements: next.announcements.map((notice) => {
    const old = previous?.announcements?.find((item) => item.id === notice.id);
    const unchanged = old && old.title === notice.title && old.text === notice.text &&
      old.pinned === notice.pinned && old.expiresAt === notice.expiresAt;
    return { ...notice, updatedAt: unchanged && old.updatedAt !== undefined ? old.updatedAt : now };
  }) };
}

export function announcementExpired(notice: TournamentAnnouncement, timezone: string | undefined, now: number): boolean {
  const localNow = notice.expiresAt ? wallClock(timezone, now) : undefined;
  return Boolean(notice.expiresAt && localNow && localNow >= notice.expiresAt);
}

export function activeAnnouncements(notices: TournamentAnnouncement[], timezone: string | undefined, now: number): TournamentAnnouncement[] {
  return notices.filter((notice) => !announcementExpired(notice, timezone, now))
    .sort((a, b) => Number(b.pinned) - Number(a.pinned) || (b.updatedAt ?? 0) - (a.updatedAt ?? 0));
}

export function announcementExpiryNeedsZone(info: TournamentInformation | undefined, timezone: string | undefined): boolean {
  return !timezone && Boolean(info?.announcements?.some((notice) => notice.expiresAt));
}
